import { store } from '@betacrew/jars-flux';
import { all, call, put, takeLatest } from 'redux-saga/effects';
import { componentKey, setActivateConfirmationModalOpen, setAssignToUserTableLoader, setDeActivateConfirmationModalOpen, setDeleteConfirmationModalOpen, setFormsStatus, setGenerateReportDrawer, setGenerateReportStates, setHighlightedPayer, setLoadingState, setPaginationState, setPayersList, setPayersReportHeaderLoadingState, setPieChartLoader, setRecipientsStatus, setReportHeaders, setSelectedPayers, setSelectedUsers, setShouldLoadPaginatedPayersList, setShowAssignToUserTable, setUsersList, toggleReportsHeader } from "./PayerListSlice"
import { addNotifications } from '../../toaster/ToasterPortalSlice';
import { TOASTER_VARIANT } from '../../../components/toast/constants';
import PayerDataService from '../../../../services/PayerDataService';
import { PAGE_STATE } from '../../constants/constants';
import Payer from '../../../../libs/utility/Payer';
import General from '../../../../libs/utility/General';
import RightsManagementDataService from '../../../../services/RightsManagementDataService';
import { PAYER_LIST_LOADING_STATES } from './Constants';

export const { getPayersList, getPaginatedPayersList, getPayersDetails, getFormAndRecipientStatus,
    assignToUser, deActivatePayer, deletePayer, getMemberUsersList, getHeadersForPayerReport, downloadPayerReport, activatePayer } = {

    getPayersList: (payload) => {
        return {
            type: "GET_PAYERS_LIST",
            payload
        }
    },
    getPaginatedPayersList: (payload) => {
        return {
            type: "GET_PAGINATED_PAYERS_LIST",
            payload
        }
    },
    getPayersDetails: (payload) => {
        return {
            type: "GET_PAYER_DETAILS",
            payload
        }
    },
    getFormAndRecipientStatus: (payload) => {
        return {
            type: "GET_FORMS_AND_RECIPIENT_STATUS",
            payload
        }
    },
    assignToUser: (payload) => {
        return {
            type: "PAYER_LISTING/ASSIGN_TO_USER",
            payload
        }
    },
    deActivatePayer: (payload) => {
        return {
            type: "PAYER_LISTING/DEACTIVATE_PAYER",
            payload
        }
    },
    deletePayer: (payload) => {
        return {
            type: "PAYER_LISTING/DELETE_PAYER",
            payload
        }
    },
    getMemberUsersList: (payload) => {
        return {
            type: "PAYER_LISTING/GET_MEMBER_USERS",
            payload
        }
    },
    getHeadersForPayerReport: (payload) => {
        return {
            type: "GET_HEADERS_FOR_PAYER_REPORT",
            payload
        }
    },
    downloadPayerReport: (payload) => {
        return {
            type: "DOWNLOAD_PAYER_REPORT",
            payload
        }
    },
    activatePayer: (payload) => {
        return {
            type: "PAYER_LISTING/ACTIVATE_PAYER",
            payload
        }
    },
}

function* getPayersListAsync() {

    try {
        yield put(setLoadingState({ state: PAGE_STATE.LOADING, message: "Fetching Payers List" }))

        const response = yield PayerDataService.getPayersList();

        const { Data, Message, Status } = response?.data;

        if (Status) {
            yield put(setPayersList(Data));
            if (Data.length) {
                yield put(setHighlightedPayer(Data[0]))
            }
        } else {
            yield put(addNotifications({ heading: "Failed to get the payers list" }));
        }

    } catch (error) {
        console.log('err: ', error)
        yield put(addNotifications({ heading: "Failed to get the payers list", message: error?.response?.data?.Message }));
    } finally {
        yield put(setLoadingState({ state: PAGE_STATE.PAGE_READY }))
    }

}

function* getPaginatedPayersListAsync(action) {

    try {
        yield put(setLoadingState({ state: PAYER_LIST_LOADING_STATES.GET_PAYER_LIST_LOADING }))

        const { PageNumber, PageSize, FilterText, IsDesc, IdNumber, highlightedPayer, selectedPayers } = action.payload;

        const idNumber = (General.removeDashesFromString(IdNumber || "")).trim()

        const params = {
            PageNumber: PageNumber || 1,
            PageSize: PageSize || 10,
            FilterText: FilterText || "",
            IdNumber: idNumber || "",
            IsDesc: IsDesc || false,
        }

        const response = yield PayerDataService.getPaginatedPayersList(params);

        const { Message, Status } = response?.data;
        const Data  = General.extractDataFromResponseData(response.data)

        if (Status) {
            yield put(setPayersList(Data));

            const mergedArray = selectedPayers.map((elementA) => {
                const foundElement = Data.find((elementB) => elementB.PayerDetailId === elementA.PayerDetailId);
                return foundElement ? foundElement : elementA;
            });
            yield put(setSelectedPayers(mergedArray));

            yield put(setPaginationState({ totalPayers: General.extractPaginationTotalFromResponse(response.data) }))

            if (Data?.length) {
                if (highlightedPayer?.PayerDetailId) {
                    const payer = Data.find(p => p?.PayerDetailId == highlightedPayer?.PayerDetailId);

                    if (payer) {
                        yield put(setHighlightedPayer(payer));
                    } else {
                        yield put(setHighlightedPayer(Data[0]));
                    }
                } else {
                    yield put(setHighlightedPayer(Data[0]));
                }
            }

        } else {
            yield put(addNotifications({ heading: "Failed to get the payers list" }));
        }

    } catch (error) {
        console.log('err: ', error)
        yield put(addNotifications({ heading: "Failed to get the payers list", message: error?.response?.data?.Message }));
    } finally {
        yield put(setLoadingState({ state: PAYER_LIST_LOADING_STATES.GET_PAYER_LIST_READY }))
    }

}

function* getPayerDetailsAsync(payload) {

    try {
        yield put(setLoadingState({ state: PAGE_STATE.LOADING, message: "Fetching Payers Details" }))
        yield put(setHighlightedPayer(payload))

    } catch (error) {
        console.log('err: ', error)
        yield put(addNotifications({ heading: "Failed to get the payers details", message: error?.response?.data?.Message }));
    } finally {
        yield put(setLoadingState({ state: PAGE_STATE.PAGE_READY }))
    }

}

function* getFormAndRecipientStatusAsync(action) {

    try {
        yield put(setPieChartLoader(true))
        yield put(setHighlightedPayer(action.payload));

        const { PayerDetailId } = action.payload;

        const [formsReponse, recipientResponse] = yield all([
            call(PayerDataService.getFormsStatus, PayerDetailId, "2022"),
            call(PayerDataService.getRecipientsStatus, PayerDetailId),
        ]);

        if (formsReponse?.data?.Status) {
            yield put(setFormsStatus(formsReponse.data?.Data));
        } else {
            yield put(addNotifications({ heading: "Failed to fetch forms status", variant: TOASTER_VARIANT.DANGER }));
        }

        if (recipientResponse?.data?.Status) {
            yield put(setRecipientsStatus(recipientResponse.data?.Data));
        } else {
            yield put(addNotifications({ heading: "Failed to fetch recipients status", variant: TOASTER_VARIANT.DANGER }));
        }

    } catch (error) {
        console.log(error)
        yield put(addNotifications({ heading: "Failed to fetch forms and recipients status", message: error?.response?.data?.Message, variant: TOASTER_VARIANT.DANGER }));
    } finally {
        yield put(setPieChartLoader(false))
    }

}

function* assignToUserAsync(action) {
    try {
        yield put(setLoadingState({ state: PAGE_STATE.LOADING, message: "Assigning to user" }));

        const { selectedPayers, highlightedPayer: { PayerDetailId, UserId }, selectedUsers } = action.payload;

        const payerIDs = [];
        const userIDs = [];

        if (selectedPayers.length) {
            selectedPayers.forEach(payer => {
                payerIDs.push(payer?.PayerDetailId)
            })
        } else {
            payerIDs.push(PayerDetailId);
        }

        if (selectedPayers.length) {
            selectedUsers.forEach(user => {
                userIDs.push(user?.UserId)
            })
        }

        const response = yield PayerDataService.assignPayers({ PayerIds: payerIDs, UserIds: userIDs });

        const { Message, Status } = response?.data;

        if (Status) {
            yield put(addNotifications({ heading: Message, message: "Payer assigned to the user", variant: TOASTER_VARIANT.SUCCESS }));
            yield put(setShowAssignToUserTable(false));
        } else {
            yield put(addNotifications({ heading: "Failed to assign to the user", message: Message }));
        }

    } catch (error) {
        console.log(error)
        yield put(addNotifications({ heading: "Failed to assign to the user", message: error?.response?.data?.Message }));
    } finally {
        yield put(setLoadingState({ state: PAGE_STATE.PAGE_READY }))
    }
}

function* deActivatePayerAsync(action) {
    try {
        yield put(setLoadingState({ state: PAYER_LIST_LOADING_STATES.DEACTIVATE_PAYER_LOADING  }));

        const { selectedPayers, highlightedPayer } = action.payload;

        const payerIDs = [];

        if (selectedPayers.length) {
            selectedPayers.forEach(payer => {
                payerIDs.push(payer?.PayerDetailId)
            })
        } else {
            payerIDs.push(highlightedPayer?.PayerDetailId);
        }

        const response = yield PayerDataService.deActivatePayer({ PayerIds: payerIDs });

        const { Message, Status } = response?.data;

        if (Status) {
            yield put(setDeActivateConfirmationModalOpen(false))
            yield put(setShouldLoadPaginatedPayersList(true));
            yield put(addNotifications({ heading: Message, message: "Deactivated Payers", variant: TOASTER_VARIANT.SUCCESS }));
        } else {
            yield put(addNotifications({ heading: "Failed to de-activate payers", message: Message }));
        }

    } catch (error) {
        console.log(error)
        yield put(addNotifications({ heading: "Failed to de-activate payers", message: error?.response?.data?.Message }));
    } finally {
        yield put(setLoadingState({ state: PAYER_LIST_LOADING_STATES.DEACTIVATE_PAYER_READY }))
    }
}

function* deletePayerAsync(action) {
    try {
        yield put(setLoadingState({ state: PAYER_LIST_LOADING_STATES.DELETE_PAYER_LOADING }));

        const { selectedPayers, highlightedPayer: { PayerDetailId } } = action.payload;

        const payerIDs = [];

        if (selectedPayers.length) {
            selectedPayers.forEach(payer => {
                payerIDs.push(payer?.PayerDetailId)
            })
        } else {
            payerIDs.push(PayerDetailId);
        }

        const response = yield PayerDataService.deletePayer({ PayerIds: payerIDs });

        const { Message, Status, Data } = response?.data;

        if (Status) {

            let message = Data.SuccessCount == 0 ? "You can not delete this payer because forms are attached with this payer." : "";
            let headingText = Data.SuccessCount == 0 ? "Payer Not Deleted" : "";
            let variant = Data.SuccessCount == 0 ? TOASTER_VARIANT.WARNING : ""

            message = Data.FailureCount > 0 && Data.SuccessCount > 0 ? `${Data.SuccessCount} Payer deleted successfully. Some might have forms created under them.` : message;
            headingText = Data.FailureCount > 0 && Data.SuccessCount > 0 ? "Some Payer Deleted" : headingText;
            variant = Data.FailureCount > 0 && Data.SuccessCount > 0 ? TOASTER_VARIANT.SUCCESS : variant

            message = Data.FailureCount == 0 ? "Payer deleted successfully." : message;
            headingText = Data.FailureCount == 0 ? "Payer Deleted" : headingText;
            variant = Data.FailureCount == 0 ? TOASTER_VARIANT.SUCCESS : variant

            yield put(addNotifications({ heading: headingText, variant: variant, message: message }));
            yield put(setShouldLoadPaginatedPayersList(true));
            yield put(setDeleteConfirmationModalOpen(false));
        } else {
            yield put(addNotifications({ heading: "Failed to delete payers", message: Message }));
        }

    } catch (error) {
        console.log(error)
        yield put(addNotifications({ heading: "Failed to delete payers", message: error?.response?.data?.Message }));
    } finally {
        yield put(setLoadingState({ state: PAYER_LIST_LOADING_STATES.DELETE_PAYER_READY }))
    }
}

function* getMemberUsersListAsync(action) {

    try {
        yield put(setAssignToUserTableLoader(true))

        const { PageNumber, PageSize, FilterText, IsDesc } = action.payload;

        const params = {
            PageNumber: PageNumber || 1,
            PageSize: PageSize || 10,
            FilterText: FilterText || "",
            IsDesc: IsDesc || false,
        }

        const response = yield RightsManagementDataService.assignFormsUsers(params);

        const { Message, Status, Data } = response?.data;

        if (Status) {
            yield put(setUsersList(Data));
            yield put(setSelectedUsers([]));
        }
        else {
            yield put(addNotifications({ heading: "Failed to get the users list", message: Message }));
            yield put(setUsersList([]));
            yield put(setSelectedUsers([]));
        }

    } catch (error) {
        console.log('err: ', error)
        yield put(addNotifications({ heading: "Failed to get the users list", message: error?.response?.data?.Message }));
    } finally {
        yield put(setAssignToUserTableLoader(false))
    }

}

function* getHeadersForPayerReportAsync(action) {
    try {
        yield put(setPayersReportHeaderLoadingState({ state: PAYER_LIST_LOADING_STATES.GET_HEADER_FOR_PAYERS_REPORT_LOADING }));

        const response = yield PayerDataService.getHeadersForPayerReport();

        const { Message, Status, Data } = response?.data;

        if (Status) {
            yield put(setReportHeaders(Data));
        } else {
            yield put(addNotifications({ heading: "Failed to fetch headers", message: Message }));
        }

    } catch (error) {
        console.log(error)
        yield put(addNotifications({ heading: "Failed to fetch headers", message: error?.response?.data?.Message }));
    } finally {
        yield put(setPayersReportHeaderLoadingState({ state: PAYER_LIST_LOADING_STATES.GET_HEADER_FOR_PAYERS_REPORT_READY }));
    }
}

function* downloadPayerReportAsync(action) {
    try {
        yield put(setLoadingState({ state: PAYER_LIST_LOADING_STATES.DOWNLOAD_PAYER_REPORT_LOADING }));

        const { reportsHeaders, payers, generateReportStates } = action.payload

        const fileds = reportsHeaders.reduce((obj, item) => {
            obj[item.Name] = item.checked || false;
            return obj;
        }, {});

        const data = {
            PayerIds: payers.map(item => item.PayerDetailId),
            PayerFileds: fileds,
            FromDate: generateReportStates.fromDate,
            ToDate: generateReportStates.toDate,
            IsXlsx: generateReportStates.extension === "CSV" ? false : true
        }

        const response = yield PayerDataService.downloadPayerReport(data);

        const { Message, Status, Data } = response?.data;

        if (Status) {
            General.downloadFile(Data, `report.${generateReportStates.extension.toString().toLowerCase()}`, generateReportStates.extension == 'CSV' && "text/csv;charset=utf-8;")

            yield put(setGenerateReportStates({ fromDate: "", toDate: "", extension: "" }))
            yield put(addNotifications({ heading: 'Generate Report', message: "Download of the payer report was successful.", variant: TOASTER_VARIANT.SUCCESS }));
            yield put(setGenerateReportDrawer(false));
            yield put(toggleReportsHeader({ isChecked: false, reportsHeaders }))
        } else {
            yield put(addNotifications({ heading: "Failed to download payer report", message: Message }));
        }

    } catch (error) {
        console.log(error)
        yield put(addNotifications({ heading: "Failed to download payer report", message: error?.response?.data?.Message }));
    } finally {
        yield put(setLoadingState({ state: PAYER_LIST_LOADING_STATES.DOWNLOAD_PAYER_REPORT_READY }))
    }
}

function* activatePayerAsync(action) {
    try {
        yield put(setLoadingState({ state: PAYER_LIST_LOADING_STATES.ACTIVATE_PAYER_LOADING }));

        const { selectedPayers, highlightedPayer = {} } = action.payload;

        const payerIDs = selectedPayers.length > 1 ? selectedPayers.map(payer => payer?.PayerDetailId) : [highlightedPayer?.PayerDetailId]

        const response = yield PayerDataService.activatePayer({ PayerIds: payerIDs });

        const { Message, Status } = response?.data;

        if (Status) {
            yield put(setShouldLoadPaginatedPayersList(true));
            yield put(addNotifications({ heading: Message, message: "Activated Payers", variant: TOASTER_VARIANT.SUCCESS }));
            yield put(setActivateConfirmationModalOpen(false));
        } else {
            yield put(addNotifications({ heading: "Failed to activate payers", message: Message }));
        }

    } catch (error) {
        console.log(error)
        yield put(addNotifications({ heading: "Failed to activate payers", message: error?.response?.data?.Message }));
    } finally {
        yield put(setLoadingState({ state: PAYER_LIST_LOADING_STATES.ACTIVATE_PAYER_READY }))
    }
}


function* rootSaga() {
    yield all([
        takeLatest(getPayersList().type, getPayersListAsync),
        takeLatest(getPaginatedPayersList().type, getPaginatedPayersListAsync),
        takeLatest(getPayersDetails().type, getPayerDetailsAsync),
        takeLatest(getFormAndRecipientStatus().type, getFormAndRecipientStatusAsync),
        takeLatest(assignToUser().type, assignToUserAsync),
        takeLatest(deActivatePayer().type, deActivatePayerAsync),
        takeLatest(deletePayer().type, deletePayerAsync),
        takeLatest(getMemberUsersList().type, getMemberUsersListAsync),
        takeLatest(getHeadersForPayerReport().type, getHeadersForPayerReportAsync),
        takeLatest(downloadPayerReport().type, downloadPayerReportAsync),
        takeLatest(activatePayer().type, activatePayerAsync),
    ]);
}

store.sagaManager.addSaga(componentKey, rootSaga);